// Hook que envuelve el reducer de la escena y expone helpers estables.

import { useCallback, useMemo, useReducer } from 'react';
import { sceneReducer, type ReorderDir, type SceneState } from './reducer';
import type { Layer, Scene } from './types';

const EMPTY: Scene = { bg: '#ffffff', ratio: 1, layers: [], selectedId: null };
const INITIAL: SceneState = { scene: EMPTY, past: [], future: [] };

export function useScene() {
  const [state, dispatch] = useReducer(sceneReducer, INITIAL);
  const scene = state.scene;

  const selected = useMemo(
    () => scene.layers.find((l) => l.id === scene.selectedId) ?? null,
    [scene],
  );

  return {
    scene,
    selected,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    init: useCallback((s: Scene) => dispatch({ type: 'INIT', scene: s }), []),
    checkpoint: useCallback(() => dispatch({ type: 'CHECKPOINT' }), []),
    addLayer: useCallback((layer: Layer) => dispatch({ type: 'ADD_LAYER', layer }), []),
    updateLayer: useCallback(
      (id: string, patch: Partial<Layer>) =>
        dispatch({ type: 'UPDATE_LAYER', id, patch }),
      [],
    ),
    removeLayer: useCallback((id: string) => dispatch({ type: 'REMOVE_LAYER', id }), []),
    select: useCallback((id: string | null) => dispatch({ type: 'SELECT', id }), []),
    reorder: useCallback(
      (id: string, dir: ReorderDir) => dispatch({ type: 'REORDER', id, dir }),
      [],
    ),
    moveLayer: useCallback(
      (from: number, to: number) => dispatch({ type: 'MOVE_LAYER', from, to }),
      [],
    ),
    setBg: useCallback((color: string) => dispatch({ type: 'SET_BG', color }), []),
    undo: useCallback(() => dispatch({ type: 'UNDO' }), []),
    redo: useCallback(() => dispatch({ type: 'REDO' }), []),
  };
}

export type SceneApi = ReturnType<typeof useScene>;
